import { LoadingStatus, ProblemPanel, Spinner, StatePanel } from '../../ui';
import { FeedGrid, FeedGridSkeleton } from '../../listing/FeedGrid';
import { GridSearch } from '../../listing/GridSearch';
import type { FeedBinding } from './useFeed';
import { useCopy } from '../../strings/useCopy';
import styles from './feed.module.css';

/**
 * One feed's content: the heading, whatever the page has to say about the list, and the list. Renders
 * props (R13) — the branch it takes is `state.kind`, and every string in it arrived formatted.
 *
 * Home and Trending both render this. They are one row in §8's page table and one shape on the wire,
 * so the alternative would be two copies drifting apart over 2c–2e.
 */
export function Feed({ binding }: { readonly binding: FeedBinding }) {
  const { state } = binding;
  const copy = useCopy().text;
  const words = copy.feed;
  const searchWords = copy.listing.search;

  return (
    <section className={styles.feed} aria-busy={state.kind === 'loading' ? true : undefined}>
      <header className={styles.head}>
        <h1 className={styles.heading}>
          {binding.heading}
          {/* A refresh in flight over rows that are already on screen — the rows stay, so the only
              honest indicator is a small one beside the title rather than a page-wide skeleton. */}
          {binding.refreshing ? <span className={styles.refreshing}><Spinner /></span> : null}
        </h1>
        <p className={styles.lede}>
          {binding.lede}
          {binding.region === null ? null : (
            <>
              {' '}
              <span className={styles.region}>{words.region(binding.region)}</span>
            </>
          )}
        </p>
      </header>

      {state.kind === 'loading' ? (
        <>
          {/* The one announcement for the whole loading state; the skeleton grid is `aria-hidden`. */}
          <LoadingStatus>{words.loading(binding.heading)}</LoadingStatus>
          <FeedGridSkeleton cards={state.skeletonCards} />
        </>
      ) : null}

      {/* No way-out link any more: the other feed page was Trending, and its rows are a section
          on this one now — a link to where the reader already is. The panel keeps its Retry. */}
      {state.kind === 'problem' ? <ProblemPanel copy={state.copy} onRetry={binding.retry} /> : null}

      {state.kind === 'empty' ? <StatePanel title={state.title} detail={state.detail} /> : null}

      {state.kind === 'ready' ? (
        <>
          {/* The controls line above the grid (CLAUDE.md) — these feeds have no filters,
              so the line is the search alone, on the right. */}
          <div className={styles.controls}>
            <GridSearch
              id="search-feed"
              value={binding.search}
              onChange={binding.setSearch}
              words={searchWords}
              className={styles.controlsSearch}
            />
          </div>
          {state.nothingMatches ? (
            <p className={styles.noMatches}>{searchWords.noMatches(binding.search.trim())}</p>
          ) : (
            <FeedGrid items={state.items} label={binding.heading} />
          )}
        </>
      ) : null}
    </section>
  );
}
